import { Box, Link, Typography, IconButton, Tooltip } from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/common/Button';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import DatePickerField from '../../../components/common/DatePickerField';
import Loading from '../../../components/common/Loading';
import SelectField from '../../../components/common/SelectField';
import StatusChip, { StatusConfig } from '../../../components/common/StatusChip';
import CustomTable from '../../../components/common/table/CustomTable';
import { Column } from '../../../components/common/table/types';
import TextField from '../../../components/common/TextField';
import { vacancyService } from '../../../services/vacancy.service';
import type {
  VacancyData,
  VacancySearchParams,
  VacancySearchResponse,
} from '../../../services/types/vacancy.types';
import { formatDate } from '../../../utils/date';
import { usePermissions } from '../../../context/PermissionContext';
import { useSidebar } from '../../../context/SidebarContext';
import Notification from '../../../components/common/Notification';
import { useNotification } from '../../../hooks/useNotification';
import { DEFAULT_ROWS_PER_PAGE } from '../../../utils/constants';
import { APP_ROUTES } from '../../../constants/routes';
import { PAGE_IDS, PERMISSION_IDS } from '../../../constants/permissions';
import { getVacancyEditState, getVacancyPublishState } from '../../../utils/vacancyLogic';

type VacancyColumn = Column<VacancyData>;

const vacancyStatusConfig: StatusConfig = {
  Active: {
    label: 'Active',
    color: '#528900',
    backgroundColor: '#5289001A',
  },
  Inactive: {
    label: 'Inactive',
    color: '#9C9C9C',
    backgroundColor: '#9C9C9C33',
  },
};

interface FetchVacanciesParams {
  vacancy_Code?: string;
  vacancy_Title?: string;
  vacancyType?: string;
  vacancy_StartDate?: string;
  vacancy_EndDate?: string;
  applicantCount?: number;
  status?: string;
  visibleStatus?:string;
  country?: string;
  page: number;
  limit: number;
  sort?: string;
}

const VacancySearch = () => {
  const navigate = useNavigate();
  const { updateMenu } = useSidebar();
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { hasPermission, pageId, setPageId, permissions } = usePermissions();
  const permissionIds = useMemo(
    () => permissions.map((p) => p.permissionId),
    [permissions],
  );
  const [refreshVacancies, setRefreshVacancies] = useState(false);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<keyof VacancyData | undefined>(
    'vacancy_StartDate',
  );
  const [searchParams, setSearchParams] = useState({
    vacancyType: '',
    code: '',
    vacancyTitle: '',
    country: '',
    startDate: '',
    endDate: '',
    applicantCount: 0,
    status: '1', // Default to Active
  });
  const [filterParams, setFilterParams] = useState({
    vacancyType: '',
    code: '',
    vacancyTitle: '',
    country: '',
    startDate: '',
    endDate: '',
    applicantCount: 0,
    status: '1', // Default to Active
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalTitle, setDeleteModalTitle] = useState('');
  const [vacancies, setVacancies] = useState<VacancyData[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cloningVacancyId, setCloningVacancyId] = useState<number | null>(null);
  const [selectedVacancyId, setSelectedVacancyId] = useState<number | null>(
    null,
  );
  const { notification, hideNotification, showNotification } =
    useNotification();
  const [isJobDeleting, setIsJobDeleting] = useState(false);
  const [deleteJobModalTitle, setDeleteJobModalTitle] = useState('');
  const [isJobDeleteModalOpen, setIsJobDeleteModalOpen] = useState(false);
  const [isJobPosting, setIsJobPosting] = useState(false);
  const [postJobModalTitle, setPostJobModalTitle] = useState('');
  const [isJobPostModalOpen, setIsJobPostModalOpen] = useState(false);
  const [, setJobPostStatus] = useState(0);
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [withdrawalModalTitle, setWithdrawalModalTitle] = useState('');
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);

  useEffect(() => {
    if (pageId !== 5) {
      setPageId(PAGE_IDS.VACANCY_SEARCH);
      return;
    }

    const fetchVacancies = async () => {
      try {
        setIsLoading(true);

        const params: FetchVacanciesParams = {
          vacancy_Code: filterParams.code ? filterParams.code : undefined,
          vacancy_Title: filterParams.vacancyTitle
            ? filterParams.vacancyTitle
            : undefined,
          vacancyType: filterParams.vacancyType
            ? filterParams.vacancyType
            : undefined,
          vacancy_StartDate: filterParams.startDate
            ? filterParams.startDate
            : undefined,
          vacancy_EndDate: filterParams.endDate
            ? filterParams.endDate
            : undefined,
          applicantCount: filterParams.applicantCount
            ? filterParams.applicantCount
            : 0,
          status: filterParams.status || '1', // Default to Active if empty
          country: filterParams.country ? filterParams.country : undefined,
          page: page + 1, // API uses 1-based pagination
          limit: rowsPerPage,
          sort: sortField ? `${sortField}:${sortDirection}` : undefined,
        };

        const response = await vacancyService.search(params as VacancySearchParams, {
          'x-token':
            'PSBrwdyDEe4tP8E1X89lEjeD3e7IpebrFtypPjwFD9S7baxiRhIxP5dyLJuqrxV0',
        });

        const data: VacancySearchResponse = response.data;
        setTotalCount(data.count);
        setRefreshVacancies(false);
        setVacancies(data.data ? data.data : []);
      } catch (error) {
        console.error('Error fetching vacancies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVacancies();
  }, [
    page,
    rowsPerPage,
    sortField,
    sortDirection,
    filterParams.code,
    filterParams.vacancyTitle,
    filterParams.vacancyType,
    filterParams.startDate,
    filterParams.endDate,
    filterParams.applicantCount,
    filterParams.status,
    filterParams.country,
    refreshVacancies,
    pageId,
    setPageId,
  ]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    setFilterParams({
      vacancyType: searchParams.vacancyType,
      code: searchParams.code,
      vacancyTitle: searchParams.vacancyTitle,
      country: searchParams.country,
      startDate: searchParams.startDate,
      endDate: searchParams.endDate,
      applicantCount: searchParams.applicantCount,
      status: searchParams.status,
    });
  };

  const handleReset = () => {
    setSearchParams({
      vacancyType: '',
      code: '',
      vacancyTitle: '',
      country: '',
      startDate: '',
      endDate: '',
      applicantCount: 0,
      status: '1', // Reset to Active
    });
    setFilterParams({
      vacancyType: '',
      code: '',
      vacancyTitle: '',
      country: '',
      startDate: '',
      endDate: '',
      applicantCount: 0,
      status: '1', // Reset to Active
    });
    setPage(0);
  };

  const handleSort = (field: keyof VacancyData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async () => {
    if (!selectedVacancyId) return;

    try {
      setIsDeleting(true);
      await vacancyService.delete(selectedVacancyId);

      // Refresh the vacancies list
      setRefreshVacancies(true);
      setTotalCount((prev) => prev - 1);
      setSelectedVacancyId(null);
    } catch (error) {
      console.error('Error deleting vacancy:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleJobPostDelete = async () => {
    if (!selectedVacancyId) return;

    try {
      setIsJobDeleting(true);
      await vacancyService.deleteJob(selectedVacancyId);

      // Refresh the vacancies list
      setRefreshVacancies(true);
      setSelectedVacancyId(null);
      showNotification(
        'Deleted Job Post from the Eyepax site successfully',
        'success',
      );
    } catch (error) {
      console.error('Error deleting job vacancy:', error);
      showNotification('Error deleting job vacancy', 'error');
    } finally {
      setIsJobDeleting(false);
      setIsJobDeleteModalOpen(false);
    }
  };

  const handleJobPosting = async () => {
    if (!selectedVacancyId) return;

    try {
      setIsJobPosting(true);
      await vacancyService.publishJob(selectedVacancyId);

      // Refresh the vacancies list
      setRefreshVacancies(true);
      setSelectedVacancyId(null);
      showNotification(
        'Job Post Published to the Eyepax site successfully',
        'success',
      );
    } catch (error: any) {
      console.error('Error posting vacancy:', error);

      if (error.response?.status === 400 && error.response?.data?.errors) {
        showNotification(
          'Please complete all required fields in the Job Description tab before publishing.',
          'error',
        );
      } else {
        showNotification('Location details are invalid. Please ensure each location has a valid city or country', 'error');
      }
    } finally {
      setIsJobPosting(false);
      setIsJobPostModalOpen(false);
    }
  };

  const handleWithdrawal = async () => {
    if (!selectedVacancyId) return;

    try {
      setIsWithdrawal(true);
      await vacancyService.withdrawApproval(selectedVacancyId);

      // Refresh the vacancies list
      setRefreshVacancies(true);
      setSelectedVacancyId(null);
      showNotification('Vacancy approval withdrawn successfully', 'success');
    } catch (error) {
      console.error('Error withdrawing vacancy approval:', error);
      showNotification('Failed to withdraw vacancy approval', 'error');
    } finally {
      setIsWithdrawal(false);
      setIsWithdrawalModalOpen(false);
    }
  };

  const openDeleteModal = (vacancyId: number, vacancyTitle: string) => {
    setSelectedVacancyId(vacancyId);
    setIsDeleteModalOpen(true);
    setDeleteModalTitle(vacancyTitle);
  };

  const openJobPostDeleteModal = (
    vacancyId: number,
    vacancyTitle: string,
    jobPostPublished: number,
  ) => {
    setSelectedVacancyId(vacancyId);
    setIsJobDeleteModalOpen(true);
    setDeleteJobModalTitle(vacancyTitle);
    setJobPostStatus(jobPostPublished);
  };

  const openJobPostingModal = (
    vacancyId: number,
    vacancyTitle: string,
    jobPostPublished: number,
  ) => {
    setSelectedVacancyId(vacancyId);
    setIsJobPostModalOpen(true);
    setPostJobModalTitle(vacancyTitle);
    setJobPostStatus(jobPostPublished);
  };

  const openWithdrawalModal = (vacancyId: number, vacancyTitle: string) => {
    setSelectedVacancyId(vacancyId);
    setIsWithdrawalModalOpen(true);
    setWithdrawalModalTitle(vacancyTitle);
  };

  const handleClone = async (vacancyId: number) => {
    try {
      setCloningVacancyId(vacancyId);
      await vacancyService.clone(vacancyId);
      setRefreshVacancies(true);
      setCloningVacancyId(null);
      showNotification('Vacancy cloned successfully', 'success');
    } catch (error) {
      console.error('Error cloning vacancy:', error);
      showNotification('Error cloning vacancy', 'error');
    } finally {
      setCloningVacancyId(null);
    }
  };

  const columns: VacancyColumn[] = [
    {
      field: 'vacancy_Title',
      headerName: 'Vacancy Title',
      sortable: true,
      width: '350px',
      type: 'text',
      renderCell: (row: VacancyData) => (
        <div
          style={{
            cursor: hasPermission(PERMISSION_IDS.VACANCY_TITLE_NAVIGATE)
              ? 'pointer'
              : 'default',
          }}
          onClick={() => {
            if (hasPermission(PERMISSION_IDS.VACANCY_TITLE_NAVIGATE)) {
              navigate(APP_ROUTES.VACANCY.VIEW(row.id));
            }
          }}
        >
          {row.vacancy_Title}
        </div>
      ),
    },
    {
      field: 'vacancy_Code',
      headerName: 'Vacancy Code',
      sortable: true,
      width: '150px',
      type: 'text',
    },
    {
      field: 'vacancy_Type',
      headerName: 'Vacancy Type',
      sortable: true,
      width: '80px',
      type: 'text',
    },
    {
      field: 'vacancy_StartDate',
      headerName: 'Start Date',
      sortable: true,
      width: '100px',
      type: 'date',
      align: 'center',
      renderCell: (row: VacancyData) => formatDate(row.vacancy_StartDate),
    },
    {
      field: 'vacancy_EndDate',
      headerName: 'End Date',
      sortable: true,
      width: '100px',
      type: 'date',
      align: 'center',
      renderCell: (row: VacancyData) => formatDate(row.vacancy_EndDate),
    },
    {
      field: 'vacancy_DueDate',
      headerName: 'Due Date',
      sortable: true,
      width: '100px',
      type: 'date',
      align: 'center',
      renderCell: (row: VacancyData) => 
        row.vacancy_DueDate ? formatDate(row.vacancy_DueDate) : ''
    },
    {
      field: 'applicantCount',
      headerName: 'Applicant Count',
      sortable: true,
      width: '100px',
      type: 'number',
      align: 'center',
      renderCell: (row: VacancyData) => (
        <div
          style={{ cursor: row.applicantCount > 0 ? 'pointer' : 'default' }}
          onClick={() => {
            if (row.applicantCount > 0) {
              navigate(`/rec/applicant/search`, {
                state: {
                  code: row.vacancy_Code,
                },
              });
              updateMenu(2);
            }
          }}
        >
          {row.applicantCount}
        </div>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      width: '80px',
      type: 'status',
      renderCell: (row: VacancyData) => (
        <StatusChip
          status={row.status === 1 ? 'Active' : 'Inactive'}
          statusConfig={vacancyStatusConfig}
        />
      ),
    },
    {
      field: 'updated_Date',
      headerName: 'Updated Date',
      sortable: true,
      width: '100px',
      type: 'date',
      align: 'center',
      renderCell: (row: VacancyData) => (
        <Tooltip title={row.updated_ByName ? `Updated by ${row.updated_ByName}` : ''} arrow>
          <div>
            {row.updated_Date ? formatDate(row.updated_Date) : ''}
          </div>
        </Tooltip>
      )
    },
    {
      field: 'jobPostPublishedDateTime',
      headerName: 'Published Date',
      sortable: true,
      width: '100px',
      type: 'date',
      align: 'center',
      renderCell: (row: VacancyData) =>
        row.jobPostPublishedDateTime
          ? formatDate(row.jobPostPublishedDateTime)
          : "",
    },
    {
      field: 'actions',
      headerName: '',
      width: '150px',
      sortable: false,
      type: 'text',
      renderCell: (row: VacancyData) => {
        const publishState = getVacancyPublishState(row, permissionIds);
        const editState = getVacancyEditState(row.approvalStatus, permissionIds);

        const onPublishIconClick = () => {
          if (publishState.action === 'unpublish') {
            openJobPostDeleteModal(row.id, row.vacancy_Title, row.jobPostPublished);
          } else if (publishState.action === 'publish') {
            openJobPostingModal(row.id, row.vacancy_Title, row.jobPostPublished);
          }
        };

        return (
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <IconButton
              size="small"
              disabled={publishState.isDisabled}
              onClick={onPublishIconClick}
              title={publishState.iconTitle}
              sx={{
                color: publishState.iconColor,
                '&:disabled': {
                  color: '#9C9C9C',
                  cursor: 'default',
                },
                '&:hover:not(:disabled)': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <PublicOutlinedIcon />
            </IconButton>
            <Link
              width="50px"
              component="button"
              sx={{
                color: '#0000008C',
                fontSize: '14px',
                fontWeight: 500,
                textDecoration: 'none',
                '&:disabled': {
                  color: '#00000066',
                  cursor: 'default',
                },
              }}
              disabled={editState.isDisabled}
              onClick={() => {
                if (editState.action === 'edit') {
                  navigate(APP_ROUTES.VACANCY.EDIT(row.id));
                } else if (editState.action === 'withdraw') {
                  openWithdrawalModal(row.id, row.vacancy_Title);
                }
              }}
            >
              Edit
            </Link>
            <Link
              width="50px"
              component="button"
              disabled={
                !hasPermission(PERMISSION_IDS.VACANCY_DELETE) ||
                row.jobPostPublished === 1
              }
              sx={{
                color: '#E9004A',
                fontSize: '14px',
                fontWeight: 500,
                textDecoration: 'none',
                '&:disabled': {
                  color: '#00000066',
                  cursor: 'default',
                },
              }}
              onClick={() => openDeleteModal(row.id, row.vacancy_Title)}
            >
              Delete
            </Link>
            <Link
              width="50px"
              component="button"
              onClick={() => handleClone(row.id)}
              disabled={
                !hasPermission(PERMISSION_IDS.VACANCY_CLONE) ||
                cloningVacancyId === row.id
              }
              sx={{
                color: '#5F8CFF',
                fontSize: '14px',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              {cloningVacancyId === row.id ? 'Cloning...' : 'Clone'}
            </Link>
          </Box>
        );
      },
    },
  ];

  return (
    <Box className="page-container">
      <Typography className="page-title">Vacancy Search</Typography>
      <Box className="search-section">
        <Box className="search-bar">
          <div style={{ flex: 1 }}>
            <SelectField
              placeholder="Vacancy Type"
              value={searchParams.vacancyType}
              width="100%"
              onChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  vacancyType: e.target.value as string,
                })
              }
              options={[
                { value: 'TAAS', label: 'TAAS' },
                { value: 'EOR', label: 'EOR' },
              ]}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Vacancy Title/Code"
              value={searchParams.vacancyTitle}
              width="100%"
              onChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  vacancyTitle: e.target.value,
                  code: e.target.value,
                })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Country"
              value={searchParams.country}
              width="100%"
              onChange={(e) =>
                setSearchParams({ ...searchParams, country: e.target.value })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <DatePickerField
              placeholder="Start Date"
              value={
                searchParams.startDate ? dayjs(searchParams.startDate) : null
              }
              width="100%"
              onChange={(date) =>
                setSearchParams({ ...searchParams, startDate: date || '' })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <DatePickerField
              placeholder="End Date"
              value={searchParams.endDate ? dayjs(searchParams.endDate) : null}
              width="100%"
              onChange={(date) =>
                setSearchParams({ ...searchParams, endDate: date || '' })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectField
              placeholder="Status"
              value={searchParams.status}
              width="100%"
              onChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  status: e.target.value as string,
                })
              }
              options={[
                { value: '1', label: 'Active' },
                { value: '0', label: 'Inactive' },
              ]}
            />
          </div>
          <Box className="search-bar-button-group">
            <Button variant="contained" onClick={handleSearch} width="120px">
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} width="120px">
              Clear
            </Button>
          </Box>
        </Box>
        {isLoading ? (
          <Loading message="Loading vacancies..." />
        ) : (
          <CustomTable
            columns={columns}
            rows={vacancies}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        )}
      </Box>

      {/* Delete Vacancy Confirmation Modal */}
      <ConfirmationModal
        cancelText="Cancel"
        confirmText="Delete"
        title="Delete Vacancy"
        onConfirm={handleDelete}
        open={isDeleteModalOpen}
        isActioning={isDeleting}
        onClose={() => setIsDeleteModalOpen(false)}
        message={`Are you sure you want to delete this vacancy <b>"${deleteModalTitle}"</b>? This action cannot be undone.`}
      />

      {/* Delete Job Post Confirmation Modal */}
      <ConfirmationModal
        cancelText="Cancel"
        confirmText="Delete"
        title="Delete Job Vacancy"
        onConfirm={handleJobPostDelete}
        open={isJobDeleteModalOpen}
        isActioning={isJobDeleting}
        onClose={() => setIsJobDeleteModalOpen(false)}
        message={`Are you sure you want to delete this job vacancy <b>"${deleteJobModalTitle}"</b> from Eyepax site? This action cannot be undone.`}
      />

      {/* Job Posting Confirmation Modal */}
      <ConfirmationModal
        cancelText="Cancel"
        confirmText="Publish"
        title="Post Job Vacancy"
        onConfirm={handleJobPosting}
        open={isJobPostModalOpen}
        isActioning={isJobPosting}
        onClose={() => setIsJobPostModalOpen(false)}
        message={`Are you sure you want to publish this job vacancy <b>"${postJobModalTitle}"</b> to Eyepax site?`}
      />

      {/* Withdrawal Vacancy Approval Modal */}
      <ConfirmationModal
        cancelText="Cancel"
        confirmText="Withdraw Approval"
        title="Withdraw Vacancy Approval"
        onConfirm={handleWithdrawal}
        open={isWithdrawalModalOpen}
        isActioning={isWithdrawal}
        onClose={() => setIsWithdrawalModalOpen(false)}
        message={`Are you sure you want to withdraw this <b>"${withdrawalModalTitle}"</b> vacancy approval?`}
      />

      {/* Notification Component */}
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default VacancySearch;
